/**
 * Every rule here is scoped to the widget's shadow root, so nothing can leak
 * into the host page and nothing on the host page can restyle the widget.
 *
 * `all: initial` on the root is the belt-and-braces part: shadow DOM already
 * blocks selector matching, but inherited properties (font, color, line-height,
 * direction) still cross the boundary.
 */
export const STYLES = `
:host {
  all: initial;
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  pointer-events: none;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  color: #1c1c24;
}

*, *::before, *::after { box-sizing: border-box; }
button { font-family: inherit; }

/* ---------------------------------------------------------------- layers */

.doc-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.doc-layer > * { pointer-events: none; }

.capture {
  position: fixed;
  inset: 0;
  pointer-events: auto;
  cursor: crosshair;
  touch-action: none;
}

.ring {
  position: fixed;
  inset: 0;
  border: 2px solid #e5484d;
  pointer-events: none;
}

/* ---------------------------------------------------------------- marks */

.badge {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e5484d;
  border: 2px solid #fff;
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.28);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: spotnote-pin-pop 0.25s cubic-bezier(0.2, 0, 0, 1);
}

.rect {
  position: absolute;
  background: rgba(250, 204, 21, 0.32);
  border: 2px solid #eab308;
  border-radius: 0;
}

.rect-draft {
  position: absolute;
  border: 2px dashed #e5484d;
  background: rgba(32, 61, 230, 0.07);
  border-radius: 0;
}

.sketch { position: absolute; left: 0; top: 0; overflow: visible; }

/* ---------------------------------------------------------------- chrome */

.hint {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 9px 16px;
  border-radius: 999px;
  white-space: nowrap;
  animation: spotnote-chip-in 0.2s ease;
  pointer-events: none;
}

.toolbar {
  position: fixed;
  bottom: 16px;
  /* Centred via auto margins, not translateX(-50%): the card-up animation
     animates the transform property and would override the centring for its
     duration - a re-render mid-session made the bar jump right by half its
     width. */
  left: 0;
  right: 0;
  margin-inline: auto;
  width: max-content;
  background: #1c1c24;
  border: none;
  box-shadow: 0 16px 32px -12px rgba(28, 28, 36, 0.5);
  padding: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: auto;
  animation: spotnote-card-up 0.2s ease;
  max-width: calc(100vw - 24px);
  flex-wrap: wrap;
  justify-content: center;
}

.mark {
  width: 26px;
  height: 26px;
  background: #2a2a34;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  flex-shrink: 0;
}

.divider { width: 1px; height: 24px; background: rgba(255, 255, 255, 0.14); flex-shrink: 0; }

.tool {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  font-size: 11px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
}
.tool[aria-pressed='true'] { background: rgba(255, 255, 255, 0.1); color: #fff; }

.icon-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 6px 8px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1;
}
.icon-btn:disabled { color: rgba(255, 255, 255, 0.25); cursor: default; }


.primary {
  background: #e5484d;
  border: none;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 10px 16px;
  cursor: pointer;
  white-space: nowrap;
}
.primary:disabled { opacity: 0.4; cursor: default; }

.secondary {
  background: #fdfcfa;
  border: 1px solid #cfc9bc;
  color: #55555f;
  font-size: 11px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 9px 14px;
  cursor: pointer;
}

.close {
  background: none;
  border: none;
  color: #b6b2a8;
  font-size: 14px;
  cursor: pointer;
  padding: 6px 8px;
}

/* Language menu: a <details> so it needs no state; opens upward. */
.lang { position: relative; display: flex; }
.lang summary {
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
}
.lang summary::-webkit-details-marker { display: none; }
.lang summary:hover, .lang[open] summary { color: #fff; }
.lang-menu {
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 120px;
  padding: 4px;
  background: #1c1c24;
  box-shadow: 0 16px 32px -12px rgba(28, 28, 36, 0.5);
}
.lang-option {
  background: none;
  border: none;
  text-align: left;
  padding: 7px 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
}
.lang-option:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
.lang-option[aria-pressed='true'] { background: rgba(255, 255, 255, 0.1); color: #fff; }

/* ---------------------------------------------------------------- launcher */

/* A square tile carrying the shuriken — the collapsed widget IS the mark. */
.launcher {
  position: fixed;
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1c1c24;
  color: #fff;
  border: none;
  padding: 0;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 16px 32px -12px rgba(28, 28, 36, 0.5);
}
.launcher.corner-bottom-right { bottom: 22px; right: 22px; }
.launcher.corner-bottom-left { bottom: 22px; left: 22px; }
.launcher.corner-top-right { top: 22px; right: 22px; }
.launcher.corner-top-left { top: 22px; left: 22px; }
.launcher.pulse { animation: spotnote-pulse-ring 2.2s infinite; }

/* ---------------------------------------------------------------- unsent count */

/* The unsent-notes counter, worn by the launcher tile and the toolbar's Send
   button alike — the same red dot the marks on the page use. The host element
   needs position: relative; the halo matches whatever the badge sits on. */
.unsent-count {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e5484d;
  color: #fff;
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 22px;
  text-align: center;
  box-shadow: 0 0 0 2px #fdfcfa;
  pointer-events: none;
}
.toolbar .primary { position: relative; }
.toolbar .unsent-count {
  top: -7px;
  right: -7px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 10px;
  line-height: 18px;
  box-shadow: 0 0 0 2px #1c1c24;
}

/* ---------------------------------------------------------------- composer */

.composer {
  position: absolute;
  width: 300px;
  background: #fdfcfa;
  border-radius: 0;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.12);
  padding: 14px;
  pointer-events: auto;
  animation: spotnote-card-up 0.18s ease;
}

.composer-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }

.dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e5484d;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.title { font-size: 13px; font-weight: 700; color: #1c1c24; }

.chip {
  font-size: 11px;
  font-weight: 600;
  color: #8a8a94;
  background: #f1eee7;
  border-radius: 0;
  padding: 2px 7px;
}

textarea {
  width: 100%;
  border: 1px solid #cfc9bc;
  border-radius: 0;
  padding: 9px 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  color: #1c1c24;
  resize: none;
  height: 70px;
}
textarea:focus, input:focus { outline: 2px solid #ef9da0; outline-offset: 0; }

.actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px; }

/* ---------------------------------------------------------------- review */

.panel {
  position: fixed;
  right: 12px;
  top: 12px;
  bottom: 12px;
  width: 340px;
  max-width: calc(100vw - 24px);
  background: #fdfcfa;
  border-radius: 0;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  animation: spotnote-card-up 0.2s ease;
}

.panel-head { padding: 18px 18px 12px; }
.panel-title { font-size: 16px; font-weight: 700; color: #1c1c24; }
.panel-sub { font-size: 13px; color: #8a8a94; margin-top: 3px; }

.note-about {
  margin: 0 18px;
  background: #fdf1f1;
  border-radius: 0;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #7d2326;
}

.panel-body {
  flex: 1;
  overflow: auto;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.review-item { border: 1px solid #e3dfd7; border-radius: 0; padding: 10px 12px; }
.review-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.review-text { font-size: 13px; line-height: 1.5; color: #2a2a34; overflow-wrap: anywhere; }

.panel-foot { padding: 14px 18px; border-top: 1px solid #e3dfd7; display: flex; gap: 10px; }
.panel-foot .secondary { flex: 1; text-align: center; padding: 10px 0; }
.panel-foot .primary { flex: 2; padding: 10px 0; }

/* ---------------------------------------------------------------- sent */

.scrim {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  animation: spotnote-fade-in 0.2s ease;
  padding: 16px;
}

.dialog {
  width: 380px;
  max-width: 100%;
  background: #fdfcfa;
  border-radius: 0;
  padding: 30px 28px;
  text-align: center;
  animation: spotnote-card-up 0.25s ease;
}

.dialog h2 { font-size: 20px; font-weight: 800; color: #1c1c24; margin: 14px 0 0; }
.dialog p { font-size: 14px; line-height: 1.6; color: #55555f; margin: 8px 0 22px; }
.dialog .primary { width: 100%; padding: 12px 0; font-size: 14px; }

.error {
  background: #fdf1f1;
  border: 1px solid #f6c5c7;
  color: #c73a3f;
  border-radius: 0;
  padding: 9px 11px;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 10px;
}

/* ---------------------------------------------------------------- motion */

@keyframes spotnote-pin-pop {
  0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}
@keyframes spotnote-chip-in {
  from { opacity: 0; transform: translate(-50%, -8px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes spotnote-card-up {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes spotnote-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes spotnote-pulse-ring {
  0% { box-shadow: 0 10px 15px -3px rgba(17,24,39,.2), 0 0 0 0 rgba(229,72,77,.45); }
  70% { box-shadow: 0 10px 15px -3px rgba(17,24,39,.2), 0 0 0 14px rgba(229,72,77,0); }
  100% { box-shadow: 0 10px 15px -3px rgba(17,24,39,.2), 0 0 0 0 rgba(229,72,77,0); }
}

@media (prefers-reduced-motion: reduce) {
  .badge, .hint, .toolbar, .composer, .panel, .scrim, .dialog, .launcher { animation: none !important; }
}
`
